import { Dispatch } from '@reduxjs/toolkit';
import { undoRedoManager } from './undoRedoManager';
import { ACTION_CATEGORIES } from './types';
import {
    createElementActionDescription,
    createPropertyChangeDescription,
    createScreenActionDescription,
    createPropertyChange,
    getPropertyDisplayName,
} from './actionDescriptions';
import {
    createUIElement,
    deleteUIElement,
    updateScreenUIElement,
    updateScreenUIElementMetadata,
    createScreen,
    deleteScreen,
    moveElementToScreen,
    moveElementToLocation,
} from '../state/screensSlice';

// Debounce configuration for different action types
const DEBOUNCE_CONFIGS = {
    PROPERTY_CHANGE: { delay: 500, groupByTarget: true },
    POSITION_CHANGE: { delay: 300, groupByTarget: true },
    RESIZE_CHANGE: { delay: 300, groupByTarget: true },
    TEXT_CHANGE: { delay: 1000, groupByTarget: true },
};

// Enhanced action creators that include undo/redo tracking

export function createElementWithUndo(
    dispatch: Dispatch,
    screenId: string,
    horizontalLocation: string,
    verticalLocation: string,
    elementType: UIElementType,
    layer: number = 0,
) {
    const elementId = `${elementType}-${Date.now()}`;

    undoRedoManager.executeAction({
        category: ACTION_CATEGORIES.ELEMENT_CREATE,
        description: createElementActionDescription(
            'create',
            elementType,
            elementId,
        ),
        targetId: elementId,
        targetName: getPropertyDisplayName(elementType),
        changes: [
            createPropertyChange(
                'element',
                null,
                { elementType, horizontalLocation, verticalLocation },
                'Element Creation',
            ),
        ],
        execute: () => {
            dispatch(
                createUIElement({
                    screenId,
                    horizontalLocation,
                    verticalLocation,
                    elementType,
                    layer,
                    elementId,
                }),
            );
        },
        undo: () => {
            dispatch(
                deleteUIElement({
                    screenId,
                    elementId,
                }),
            );
        },
    });
}

export function deleteElementWithUndo(
    dispatch: Dispatch,
    elementId: string,
    screenId: string,
    elementData: any, // Using any since we need the full element data for restoration
) {
    undoRedoManager.executeAction({
        category: ACTION_CATEGORIES.ELEMENT_DELETE,
        description: createElementActionDescription(
            'delete',
            elementData.type,
            elementId,
        ),
        targetId: elementId,
        targetName: getPropertyDisplayName(elementData.type),
        changes: [
            createPropertyChange(
                'element',
                elementData,
                null,
                'Element Deletion',
            ),
        ],
        execute: () => {
            dispatch(
                deleteUIElement({
                    screenId,
                    elementId,
                }),
            );
        },
        undo: () => {
            // For undo, we need to recreate the element with its original properties and ID
            dispatch(
                createUIElement({
                    screenId,
                    horizontalLocation: elementData.horizontalLocation,
                    verticalLocation: elementData.verticalLocation,
                    elementType: elementData.type,
                    layer: elementData.layer || 0,
                    elementId: elementData.id,
                }),
            );
        },
    });
}

export function updateElementPropertyWithUndo(
    dispatch: Dispatch,
    elementId: string,
    screenId: string,
    propertyPath: string,
    newValue: any,
    oldValue: any,
    elementType: UIElementType,
    debounced: boolean = true,
) {
    // Execute the Redux action immediately for snappy UI updates
    const executeReduxAction = (value: any) => {
        if (propertyPath.startsWith('metadata.')) {
            // Use metadata-specific action
            const metadataKey = propertyPath.replace('metadata.', '');
            dispatch(
                updateScreenUIElementMetadata({
                    screenId,
                    elementId,
                    key: metadataKey,
                    value,
                }),
            );
        } else {
            // Use general element update action
            dispatch(
                updateScreenUIElement({
                    screenId,
                    elementId,
                    key: propertyPath,
                    value,
                }),
            );
        }
    };

    // Execute immediately for responsive UI
    executeReduxAction(newValue);

    // Create action params for history tracking
    const targetName = getPropertyDisplayName(elementType);
    const changes = [createPropertyChange(propertyPath, oldValue, newValue)];

    const actionParams = {
        category: ACTION_CATEGORIES.ELEMENT_PROPERTY,
        description: createPropertyChangeDescription(
            'element',
            targetName,
            changes,
        ),
        targetId: elementId,
        targetName,
        changes,
        execute: () => {
            // This is only called during redo operations
            executeReduxAction(newValue);
        },
        undo: () => {
            // This is called during undo operations
            executeReduxAction(oldValue);
        },
    };

    if (debounced) {
        const debounceConfig = propertyPath.includes('text')
            ? DEBOUNCE_CONFIGS.TEXT_CHANGE
            : DEBOUNCE_CONFIGS.PROPERTY_CHANGE;

        // Only debounce the history recording, not the execution
        undoRedoManager.recordActionDebounced(actionParams, debounceConfig);
    } else {
        // For non-debounced actions, just add to history immediately (already executed above)
        undoRedoManager.recordAction(actionParams);
    }
}

export function copyElementWithUndo(
    dispatch: Dispatch,
    sourceElement: UIElement,
    targetScreenId: string,
    targetHorizontalLocation: string,
    targetVerticalLocation: string,
    insertIndex?: number,
) {
    // Generate new element ID
    const newElementId = `${sourceElement.type}-${Date.now()}`;

    // Create the new element data
    const newElement: UIElement = {
        ...sourceElement,
        id: newElementId,
        horizontalLocation: targetHorizontalLocation as any,
        verticalLocation: targetVerticalLocation as any,
        metadata: { ...sourceElement.metadata },
    };

    undoRedoManager.executeAction({
        category: ACTION_CATEGORIES.ELEMENT_CREATE,
        description: createElementActionDescription(
            'copy',
            sourceElement.type,
            newElementId,
        ),
        targetId: newElementId,
        targetName: getPropertyDisplayName(sourceElement.type),
        changes: [
            createPropertyChange('element', null, newElement, 'Element Copy'),
        ],
        execute: () => {
            dispatch(
                createUIElement({
                    screenId: targetScreenId,
                    horizontalLocation: targetHorizontalLocation,
                    verticalLocation: targetVerticalLocation,
                    elementType: sourceElement.type,
                    layer: sourceElement.layer || 0,
                    elementId: newElementId,
                }),
            );

            // Set the metadata after creation
            if (sourceElement.metadata) {
                Object.keys(sourceElement.metadata).forEach((key) => {
                    if (key !== 'id' && key !== 'type') {
                        dispatch(
                            updateScreenUIElementMetadata({
                                screenId: targetScreenId,
                                elementId: newElementId,
                                key,
                                value: (sourceElement.metadata as any)[key],
                            }),
                        );
                    }
                });
            }
        },
        undo: () => {
            dispatch(
                deleteUIElement({
                    screenId: targetScreenId,
                    elementId: newElementId,
                }),
            );
        },
    });
}

export function moveElementWithUndo(
    dispatch: Dispatch,
    elementId: string,
    fromScreenId: string,
    toScreenId: string,
    newLocation: { horizontal: string; vertical: string },
    oldLocation: { horizontal: string; vertical: string },
    elementType: UIElementType,
) {
    const targetName = getPropertyDisplayName(elementType);
    const changes = [
        createPropertyChange(
            'horizontalLocation',
            oldLocation.horizontal,
            newLocation.horizontal,
        ),
        createPropertyChange(
            'verticalLocation',
            oldLocation.vertical,
            newLocation.vertical,
        ),
    ];

    if (fromScreenId !== toScreenId) {
        changes.push(
            createPropertyChange(
                'screenId',
                fromScreenId,
                toScreenId,
                'Screen',
            ),
        );
    }

    undoRedoManager.executeActionDebounced(
        {
            category: ACTION_CATEGORIES.ELEMENT_MOVE,
            description: createElementActionDescription(
                'move',
                elementType,
                elementId,
                fromScreenId !== toScreenId ? 'to different screen' : '',
            ),
            targetId: elementId,
            targetName,
            changes,
            execute: () => {
                if (fromScreenId !== toScreenId) {
                    dispatch(
                        moveElementToScreen({
                            elementId,
                            fromScreenId,
                            toScreenId,
                        }),
                    );
                }
                dispatch(
                    moveElementToLocation({
                        elementId,
                        screenId: toScreenId,
                        horizontalLocation: newLocation.horizontal,
                        verticalLocation: newLocation.vertical,
                    }),
                );
            },
            undo: () => {
                if (fromScreenId !== toScreenId) {
                    dispatch(
                        moveElementToScreen({
                            elementId,
                            fromScreenId: toScreenId,
                            toScreenId: fromScreenId,
                        }),
                    );
                }
                dispatch(
                    moveElementToLocation({
                        elementId,
                        screenId: fromScreenId,
                        horizontalLocation: oldLocation.horizontal,
                        verticalLocation: oldLocation.vertical,
                    }),
                );
            },
        },
        DEBOUNCE_CONFIGS.POSITION_CHANGE,
    );
}

export function createScreenWithUndo(
    dispatch: Dispatch,
    screenData: ScreenDefinition,
) {
    undoRedoManager.executeAction({
        category: ACTION_CATEGORIES.SCREEN_CREATE,
        description: createScreenActionDescription('create', screenData.id),
        targetId: screenData.id,
        targetName: 'Screen',
        changes: [
            createPropertyChange('screen', null, screenData, 'Screen Creation'),
        ],
        execute: () => {
            dispatch(createScreen(screenData));
        },
        undo: () => {
            dispatch(deleteScreen({ screenId: screenData.id }));
        },
    });
}

export function deleteScreenWithUndo(
    dispatch: Dispatch,
    screenId: string,
    screenData: ScreenDefinition,
) {
    undoRedoManager.executeAction({
        category: ACTION_CATEGORIES.SCREEN_DELETE,
        description: createScreenActionDescription('delete', screenId),
        targetId: screenId,
        targetName: 'Screen',
        changes: [
            createPropertyChange('screen', screenData, null, 'Screen Deletion'),
        ],
        execute: () => {
            dispatch(deleteScreen({ screenId }));
        },
        undo: () => {
            dispatch(createScreen(screenData));
        },
    });
}

export function updateScreenPropertyWithUndo(
    dispatch: Dispatch,
    screenId: string,
    propertyPath: string,
    newValue: any,
    oldValue: any,
    debounced: boolean = true,
) {
    const changes = [createPropertyChange(propertyPath, oldValue, newValue)];

    const actionParams = {
        category: ACTION_CATEGORIES.SCREEN_PROPERTY,
        description: createPropertyChangeDescription(
            'screen',
            'Screen',
            changes,
        ),
        targetId: screenId,
        targetName: 'Screen',
        changes,
        execute: () => {
            // This would need to be implemented based on your screen update action
            console.log(
                'Update screen property:',
                screenId,
                propertyPath,
                newValue,
            );
        },
        undo: () => {
            // This would need to be implemented based on your screen update action
            console.log(
                'Undo screen property:',
                screenId,
                propertyPath,
                oldValue,
            );
        },
    };

    if (debounced) {
        undoRedoManager.executeActionDebounced(
            actionParams,
            DEBOUNCE_CONFIGS.PROPERTY_CHANGE,
        );
    } else {
        undoRedoManager.executeAction(actionParams);
    }
}

// Helper function to get nested property value
export function getNestedProperty(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj);
}
